import React from 'react';
import { useLocation } from 'react-router-dom';
import { TopNav } from './nav/TopNav';
import { SubTabsPageHeader } from './nav/PageHeader';
import {
  getAccountSettingsSection,
  getActiveAccountSettingsTab,
  LEGACY_ACCOUNT_SETTINGS_TABS,
  PROFILE_SETTINGS_TABS,
  signingTabsFor,
  TEAM_SETTINGS_TABS,
} from './nav/navModel';
import { useFeatureFlag } from '../../~reusables/hooks/useFeatureFlag';
import { useScrollToHash } from './nav/useScrollToHash';
import { ContentContainer, PageWrapper, StaticCrumb } from './nav/NavStyles';
import {
  useManagesTeamV2Signing,
  useSettingsTeam,
  type SettingsTeam,
} from '../../~reusables/hooks/useSettingsTeam';

interface AccountLayoutProps {
  children: React.ReactNode;
  /** Overrides the crumb, e.g. for "App unavailable". */
  pageTitle?: string;
}

const SETTINGS_SECTIONS = {
  team: {
    title: 'Team settings',
    navLabel: 'Team settings sections',
    tabs: TEAM_SETTINGS_TABS,
  },
  profile: {
    title: 'Your profile',
    navLabel: 'Profile sections',
    tabs: PROFILE_SETTINGS_TABS,
  },
};

/**
 * Team settings tabs for the team being shown. In a team they joined, people
 * see Members, Billing and Signing only with the permissions those pages
 * need (Admins have every permission).
 */
export const getTeamSettingsTabs = (team: SettingsTeam | null) =>
  TEAM_SETTINGS_TABS.filter((tab) => {
    if (!team || team.isOwnTeam) return true;
    if (tab.key === 'user-management') return team.permissions.canManageUsers;
    if (tab.key === 'billing') return team.permissions.canManageBilling;
    if (tab.key === 'signing') return team.permissions.canManageSigning;
    return true;
  }).map((tab) => ({ ...tab, to: `${tab.to}${team?.search ?? ''}` }));

const AccountLayout: React.FC<AccountLayoutProps> = ({
  children,
  pageTitle,
}) => {
  const { pathname } = useLocation();
  useScrollToHash();
  const activeSettingsTab = getActiveAccountSettingsTab(pathname);
  const section = pageTitle ? null : getAccountSettingsSection(pathname);
  const team = useSettingsTeam();
  const hasTeams = useFeatureFlag('accountTeams');
  const v2Apps = useFeatureFlag('v2Apps');
  const managesTeamSigning = useManagesTeamV2Signing(team);
  const baseSettings = section ? SETTINGS_SECTIONS[section] : null;
  let settings =
    section === 'team' && baseSettings
      ? {
          ...baseSettings,
          title:
            team && !team.isOwnTeam
              ? `${team.teamName} settings`
              : baseSettings.title,
          tabs: getTeamSettingsTabs(team),
        }
      : baseSettings;
  if (!hasTeams && section) {
    settings = {
      title: 'Account settings',
      navLabel: 'Account settings sections',
      tabs: LEGACY_ACCOUNT_SETTINGS_TABS,
    };
  }

  if (settings) {
    // v2 is the managed account's (the server checks the team's flag), so a
    // member who manages signing for a team using v2 sees it without the flag
    // of their own. Not every Admin: they all have the permission.
    const signing = v2Apps || managesTeamSigning === true;
    settings = { ...settings, tabs: signingTabsFor(settings.tabs, signing) };
  }

  let crumb = 'All apps';
  if (pageTitle) crumb = pageTitle;
  else if (settings) crumb = settings.title;

  return (
    <PageWrapper>
      <TopNav
        crumbs={
          <StaticCrumb aria-current={settings ? undefined : 'page'}>
            {crumb}
          </StaticCrumb>
        }
      />
      <ContentContainer>
        {settings && (
          <SubTabsPageHeader
            title={settings.title}
            navLabel={settings.navLabel}
            tabs={settings.tabs}
            activeKey={activeSettingsTab}
          />
        )}
        {children}
      </ContentContainer>
    </PageWrapper>
  );
};

export default AccountLayout;
